import { useCallback, useMemo, useRef } from 'react';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import useSideDetail from '#/hooks/useSideDetail';
import ListAddButton from '#/modules/commonComponents/table/ListAddButton';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import CustomerSideMsg from '#/modules/crmMgmt/customer/components/CustomerSideMsg';
import { buildCustomerColumns } from '#/modules/crmMgmt/customer/data/customerColumns';
import { getCustomerApiErrorMessage } from '#/modules/crmMgmt/customer/helper/apiError';
import useCustomerList from '#/modules/crmMgmt/customer/hooks/useCustomerList';
import { useCan } from '#/modules/infra/auth/useCan';
import PageShell from '#/modules/infra/layout/PageShell';
import SideMsgLayout from '#/modules/infra/layout/SideMsgLayout';

const CUSTOMER_SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  taxIdNumber: '統編',
  status: '狀態',
  remark: '備註',
};

const CustomerListPage = () => {
  const listRef = useRef(null);
  const { displayData, setSelectedData } = useSideDetail();
  const canCreate = useCan(PERM.CUSTOMER_CREATE);
  const canUpdate = useCan(PERM.CUSTOMER_UPDATE);

  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    removeSortAt,
    resetListQuery,
  } = useCustomerList();

  const columns = useMemo(
    () => buildCustomerColumns({
      filters: listQuery.filters || {},
      onFilterChange: setFilter,
      canShowEdit: () => canUpdate,
    }),
    [listQuery.filters, setFilter, canUpdate],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const handleRowClick = useCallback((row, isSelected) => {
    if (isSelected) {
      setSelectedData(row.original);
      return;
    }

    setSelectedData(null);
  }, [setSelectedData]);

  const headerAction = canCreate ? (
    <ListAddButton to={ROUTES.customerCreate} />
  ) : null;

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <h1 className="page_title shrink-0">
          <span className="border-l-5 border-primary-400 pl-2.5">客戶管理</span>
        </h1>
        <SideMsgLayout>
          <div className="table-panel min-h-0 xl:h-full">
            <ListTablePanel
              ref={listRef}
              tableKey="crm.customer.list"
              columns={columns}
              rows={rows}
              pagination={pagination}
              isLoading={isLoading}
              isError={isError}
              errorMessage={isError ? getCustomerApiErrorMessage(error, '載入列表失敗') : undefined}
              onRetry={isError ? () => { refetch(); } : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={handleRowClick}
              onSearch={setKeyword}
              onReset={resetListQuery}
              initialKeyword={listQuery.keyword}
              searchPlaceholder="搜尋代號、名稱或統編"
              headerAction={headerAction}
              listQuery={listQuery}
              filterStatusFields={filterStatusFields}
              sortFieldLabels={CUSTOMER_SORT_FIELD_LABELS}
              onFilterChange={setFilter}
              onKeywordClear={() => setKeyword('')}
              onSortRemove={removeSortAt}
            />
          </div>

          <CustomerSideMsg
            displayData={displayData}
            setSelectedData={setSelectedData}
          />
        </SideMsgLayout>
      </div>
    </PageShell>
  );
};

export default CustomerListPage;
